import React, { useRef, useState } from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { analyzeContent, retirerMarksDeStyle, styleEffectifTexte } from '../utils/richTextModel';
import { canFitFrame, fitSelectionToContent } from '../utils/fitFrame';
import FontPicker from './FontPicker';
import RegulatoryInspectorBanner from './RegulatoryInspectorBanner';
import ReglagesImagePanel from './ReglagesImagePanel';
import ChampCouleur from './ChampCouleur';
import { SYMBOLOGIES_CARREES } from './canvas/BarcodeElement';
import PanneauDecoupe from './PanneauDecoupe';
import { estDecoupe } from '../utils/decoupe';

const SYMBOLOGIES = [
    { value: 'ean13', label: 'EAN-13' },
    { value: 'ean8', label: 'EAN-8' },
    { value: 'upca', label: 'UPC-A' },
    { value: 'code128', label: 'Code 128' },
    { value: 'code39', label: 'Code 39' },
    { value: 'qrcode', label: 'QR Code' },
    { value: 'datamatrix', label: 'Data Matrix' },
    { value: 'pdf417', label: 'PDF417' },
];

// Noms français des types de calque. `el.type` reste la valeur interne
// ('shape', 'barcode'…), employée comme clé dans tout le store : le titre de
// l'inspecteur affichait cette valeur telle quelle, soit « Propriétés (shape) ».
const LIBELLES_TYPE = {
    text: 'texte',
    image: 'image',
    shape: 'forme',
    barcode: 'code-barres',
    svg: 'vectoriel',
    group: 'groupe',
    guide: 'repère',
};

// Libellés lus par l'utilisateur pour les alignements de texte. Les valeurs
// internes ('left', 'center', 'right') partent dans le calque et dans le contenu
// Tiptap : elles ne bougent pas. Seul l'affichage est traduit — auparavant
// l'infobulle était construite depuis la valeur et annonçait « Aligner left ».
const LIBELLES_ALIGNEMENT = {
    left: 'Aligner à gauche',
    center: 'Centrer le texte',
    right: 'Aligner à droite',
};

/**
 * Calcule le check digit (chiffre de contrôle) d'un code-barres standard.
 * Formule commune EAN13 / EAN8 / UPC-A : on multiplie chaque chiffre par
 * un poids (alternance 1/3 ou 3/1 selon la symbologie), on somme, et le
 * check digit est le complément à 10 du dernier chiffre de cette somme.
 *
 * @param {string} digits - chaîne de chiffres (sans le check digit)
 * @param {string} symbology - 'ean13' | 'ean8' | 'upca'
 * @returns {string} - 1 chiffre, ou '' si la saisie est incomplète
 */
const computeCheckDigit = (digits, symbology) => {
    const cleaned = (digits || '').replace(/\D/g, '');
    const expectedLen = symbology === 'ean13' ? 12 : symbology === 'ean8' ? 7 : 11;
    if (cleaned.length !== expectedLen) return '';

    let sum = 0;
    if (symbology === 'ean13') {
        // EAN13 : positions impaires (1,3,5...) × 1, positions paires (2,4,6...) × 3
        for (let i = 0; i < 12; i++) {
            const d = parseInt(cleaned[i], 10);
            sum += (i % 2 === 0) ? d : d * 3;
        }
    } else if (symbology === 'ean8') {
        // EAN8 : positions impaires × 3, positions paires × 1
        for (let i = 0; i < 7; i++) {
            const d = parseInt(cleaned[i], 10);
            sum += (i % 2 === 0) ? d * 3 : d;
        }
    } else {
        // UPC-A : positions impaires × 3, positions paires × 1
        for (let i = 0; i < 11; i++) {
            const d = parseInt(cleaned[i], 10);
            sum += (i % 2 === 0) ? d * 3 : d;
        }
    }
    return String((10 - (sum % 10)) % 10);
};

// Un code-barres incomplet porte un espace à chaque position non saisie, pour
// que les chiffres gardent leur place. Ceux de queue ne décrivent aucun trou :
// on ne les propage pas dans `el.text`, qui part à l'export.
const trimTrailingBlanks = (s) => s.replace(/\s+$/, '');

const BarcodeDigitInputs = ({ value, length, onChange, symbology }) => {
    const inputsRef = React.useRef([]);
    // Avant, le remplissage se faisait avec la chaîne VIDE (`padEnd(length, '')`),
    // qui ne remplit rien : sur un code incomplet, le tableau restait plus court
    // que la grille, et taper dans une case située au-delà du dernier chiffre
    // saisi refermait les trous au `join('')` — le chiffre atterrissait à une
    // autre position que celle visée. L'espace est le trou explicite du code,
    // celui que l'affichage et le test de complétude reconnaissent plus bas.
    const digits = (value || '').padEnd(length, ' ').split('').slice(0, length);

    // Une case vidée redevient un espace, jamais une chaîne vide : sinon le
    // `join('')` referme le trou et décale tous les chiffres suivants d'un cran
    // vers la gauche. Les espaces de queue, eux, ne portent aucune information.
    const emit = (arr) => onChange(trimTrailingBlanks(arr.join('')));

    const handleChange = (index, char) => {
        const val = char.replace(/[^0-9]/g, '');
        if (!val) {
            const newArr = [...digits];
            newArr[index] = ' ';
            emit(newArr);
            return;
        }

        if (val.length > 1) {
            const pasted = val.substring(0, length - index).split('');
            const newArr = [...digits];
            for (let i = 0; i < pasted.length; i++) {
                if (index + i < length) newArr[index + i] = pasted[i];
            }
            emit(newArr);
            const nextFocus = Math.min(index + pasted.length, length - 1);
            inputsRef.current[nextFocus]?.focus();
            return;
        }

        const newArr = [...digits];
        newArr[index] = val;
        emit(newArr);

        if (index < length - 1 && val) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Une case non saisie vaut désormais une ESPACE et non une chaîne vide (le
        // remplissage sert à garder les positions, cf. `padEnd`). Or une espace est
        // truthy : tester la seule falsité aurait empêché Retour arrière de remonter
        // dans la grille, et il aurait fallu cliquer case par case.
        if (e.key === 'Backspace' && !digits[index]?.trim() && index > 0) {
            inputsRef.current[index - 1]?.focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputsRef.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < length - 1) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    // Check digit calculé automatiquement (affiché en gris à droite)
    const checkDigit = computeCheckDigit(digits.join(''), symbology);
    const isComplete = digits.every(d => d && d !== ' ');

    return (
        <div className="bg-black/20 p-2 rounded border border-white/10">
            <div className="flex flex-wrap gap-[3px] justify-center items-center">
                {Array.from({ length }).map((_, i) => (
                    <input
                        key={i}
                        ref={el => inputsRef.current[i] = el}
                        type="text"
                        // Chaque case ne porte qu'UN chiffre, mais elle en acceptait
                        // autant que le code entier (12 en EAN-13) : retaper sur une
                        // case déjà remplie produisait deux caractères, pris pour un
                        // collage, et le second chiffre écrasait la case suivante.
                        // Le collage d'un code complet, lui, passe par onPaste.
                        maxLength={1}
                        // Avec `maxLength={1}`, le navigateur REFUSE la frappe sur une
                        // case déjà remplie : sans cette sélection au focus, corriger
                        // un chiffre imposait un Retour arrière préalable.
                        onFocus={(e) => e.target.select()}
                        value={digits[i] === ' ' ? '' : (digits[i] || '')}
                        onChange={(e) => handleChange(i, e.target.value)}
                        onPaste={(e) => {
                            e.preventDefault();
                            handleChange(i, e.clipboardData.getData('text'));
                        }}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        className="w-[18px] h-6 text-center bg-black/20 border border-white/5 rounded-sm text-xs font-mono outline-none focus:border-white text-white placeholder-gray-600"
                        placeholder="-"
                    />
                ))}
                {/* Check digit calculé automatiquement — visualisation seulement */}
                <span className="text-white/30 text-xs font-mono mx-1 select-none">·</span>
                <div
                    className="w-[18px] h-6 flex items-center justify-center bg-forge-mauve/15 border border-forge-mauve/30 rounded-sm text-xs font-mono text-forge-mauve select-none"
                    title="Chiffre de contrôle (calculé automatiquement)"
                >
                    {isComplete ? checkDigit : '?'}
                </div>
            </div>
            <p className="text-[9px] text-white/40 font-mono mt-1.5 text-center tracking-wide">
                {isComplete
                    ? <>code complet · le dernier chiffre <span className="text-forge-mauve">{checkDigit}</span> est calculé</>
                    : `saisissez les ${length} chiffres de votre code (le ${length + 1}e est calculé)`
                }
            </p>
        </div>
    );
};


// Convert hex to HSL, shift hue, convert back
const hexToHsl = (hex) => {
    let r = parseInt(hex.slice(1,3),16)/255, g = parseInt(hex.slice(3,5),16)/255, b = parseInt(hex.slice(5,7),16)/255;
    const max = Math.max(r,g,b), min = Math.min(r,g,b), l = (max+min)/2;
    let h = 0, s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d/(2-max-min) : d/(max+min);
        if (max === r) h = ((g-b)/d + (g < b ? 6 : 0))/6;
        else if (max === g) h = ((b-r)/d + 2)/6;
        else h = ((r-g)/d + 4)/6;
    }
    return [h*360, s*100, l*100];
};
const hslToHex = (h, s, l) => {
    h = ((h % 360) + 360) % 360; s /= 100; l /= 100;
    const a = s * Math.min(l, 1-l);
    const f = n => { const k = (n + h/30) % 12; return l - a * Math.max(Math.min(k-3, 9-k, 1), -1); };
    const toHex = x => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
};
const shiftStopsHue = (stops, shift) => stops.map(s => {
    const [h, sat, l] = hexToHsl(s.color);
    return { ...s, color: hslToHex(h + shift, sat, l) };
});

// `allowGradient` à false ne laisse que le sélecteur de couleur unie. Il sert aux
// calques dont le rendu ne sait lire qu'une seule couleur (le code-barres) : les
// onglets Linéaire / Radial y étaient affichés et cliquables sans rien changer,
// ni à l'écran ni à l'export.
const GradientEditor = ({ fillType, gradient, color, onChangeFillType, onChangeColor, onChangeGradient, allowGradient = true }) => {
    const stops = gradient?.stops || [{color: color || '#000000', position: 0}, {color: '#ffffff', position: 100}];
    const angle = gradient?.angle !== undefined ? gradient?.angle : 90;
    const gradType = gradient?.type || 'linear';

    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const railRef = React.useRef(null);
    const [draggingIndex, setDraggingIndex] = React.useState(null);
    const [hueShift, setHueShift] = React.useState(0);
    // `angleDraft` porte le texte tapé dans le champ Angle tant qu'il est édité :
    // sans lui, un champ vidé se remplissait aussitôt de la valeur du dégradé et
    // il devenait impossible de retaper l'angle sans le remettre à zéro.
    const [angleDraft, setAngleDraft] = React.useState(null);
    const [angleMsg, setAngleMsg] = React.useState('');

    const updateStops = (newStops) => {
        onChangeGradient({ type: gradType, angle, stops: newStops });
    };
    const updateType = (newType) => {
        onChangeGradient({ type: newType, angle, stops });
    };

    const handleRailClick = (e) => {
        if (e.target !== railRef.current && e.target.dataset.type !== 'rail-bg') return;
        const rect = railRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const position = Math.round((x / rect.width) * 100);
        
        const newStops = [...stops, { color: stops[selectedIndex]?.color || '#ffffff', position }];
        updateStops(newStops);
        setSelectedIndex(newStops.length - 1);
    };

    const handleHandleMouseDown = (e, index) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDraggingIndex(index);
    };

    React.useEffect(() => {
        const handleMouseMove = (e) => {
            if (draggingIndex === null || !railRef.current) return;
            const rect = railRef.current.getBoundingClientRect();
            let x = e.clientX - rect.left;
            const position = Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
            
            const newStops = [...stops];
            newStops[draggingIndex] = { ...newStops[draggingIndex], position };
            updateStops(newStops);
        };
        const handleMouseUp = () => setDraggingIndex(null);
        
        if (draggingIndex !== null) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingIndex, stops]);

    const sortedStops = [...stops].sort((a,b) => a.position - b.position);
    const railGradient = `linear-gradient(90deg, ${sortedStops.map(s => `${s.color} ${s.position}%`).join(', ')})`;

    // Hue shift handler
    const applyHueShift = (shift) => {
        const newStops = shiftStopsHue(stops, shift - hueShift);
        setHueShift(shift);
        updateStops(newStops);
    };

    return (
        <div className="mt-1 p-2 bg-black/20 border-y border-white/5 rounded-xl shadow-inner">
            {allowGradient && (
            <div className="flex mb-3 bg-black/50 p-1 rounded-lg border border-white/5 shadow-inner">
                <button
                    onClick={() => onChangeFillType('solid')}
                    className={`flex-1 text-[9px] py-1.5 font-mono uppercase rounded-md transition-all tracking-wider ${fillType !== 'gradient' ? 'bg-white text-black font-bold shadow-md' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                >
                    Uni
                </button>
                <button
                    onClick={() => { onChangeFillType('gradient'); updateType('linear'); }}
                    className={`flex-1 text-[9px] py-1.5 font-mono uppercase rounded-md transition-all tracking-wider ${fillType === 'gradient' && gradType === 'linear' ? 'bg-white text-black font-bold shadow-md' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                >
                    Linéaire
                </button>
                <button
                    onClick={() => { onChangeFillType('gradient'); updateType('radial'); }}
                    className={`flex-1 text-[9px] py-1.5 font-mono uppercase rounded-md transition-all tracking-wider ${fillType === 'gradient' && gradType === 'radial' ? 'bg-white text-black font-bold shadow-md' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                >
                    Radial
                </button>
            </div>
            )}

            {(!allowGradient || fillType !== 'gradient') ? (
                <div className="px-1 my-2">
                    <ChampCouleur couleur={color} onChange={onChangeColor} titre="Couleur de remplissage" />
                </div>
            ) : (
                <div className="space-y-4 my-2 px-1">
                    <div className="relative h-6 rounded-full border border-white/20 mb-5 overflow-visible shadow-inner" ref={railRef} onMouseDown={handleRailClick}>
                        <div data-type="rail-bg" className="absolute inset-0 rounded-full pointer-events-none" style={{ background: railGradient }} />
                        {stops.map((stop, i) => (
                            <div 
                                key={i}
                                onMouseDown={(e) => handleHandleMouseDown(e, i)}
                                className={`absolute top-1/2 -ml-[10px] w-[20px] h-[20px] rounded-full border-[3px] cursor-pointer shadow-[0_0_10px_rgba(0,0,0,0.5)] transition-transform ${selectedIndex === i ? 'border-white z-10 scale-125' : 'border-white/50 z-0 hover:scale-110'}`}
                                style={{ left: `${stop.position}%`, transform: 'translateY(-50%)', backgroundColor: stop.color }}
                                title={`${stop.position}%`}
                            />
                        ))}
                    </div>

                    {stops[selectedIndex] && (
                        <div className="flex items-center justify-between bg-black/40 p-1.5 rounded border border-white/5">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] text-white/40 font-mono uppercase">Couleur</span>
                                <ChampCouleur
                                    couleur={stops[selectedIndex].color}
                                    taille="compact"
                                    titre="Couleur de cette étape"
                                    onChange={(hex) => {
                                        const newStops = [...stops];
                                        newStops[selectedIndex] = { ...newStops[selectedIndex], color: hex };
                                        updateStops(newStops);
                                    }}
                                />
                                <span className="text-[10px] font-mono w-6 text-right">{stops[selectedIndex].position}%</span>
                            </div>
                            <button 
                                onClick={() => {
                                    if(stops.length <= 2) return;
                                    const newStops = stops.filter((_, i) => i !== selectedIndex);
                                    updateStops(newStops);
                                    setSelectedIndex(0);
                                }}
                                disabled={stops.length <= 2}
                                className="text-[10px] text-white/40 hover:text-red-400 font-mono disabled:opacity-30 transition-colors"
                                title="Supprimer la couleur"
                            >
                                Suppr
                            </button>
                        </div>
                    )}

                    {/* Angle — uniquement pour lineaire */}
                    {gradType === 'linear' && (
                        <div className="pt-2 border-t border-white/5">
                            <label className="block text-[10px] text-white/40 font-mono mb-1.5 uppercase">Angle</label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="number"
                                    // Le champ affiche la frappe en cours tant qu'il est édité.
                                    // Avant, il était collé sur `angle` et la valeur passait par
                                    // `Number(e.target.value) || 0` : vider le champ pour retaper
                                    // envoyait 0 au dégradé (`Number('')` vaut 0, et le `|| 0`
                                    // rattrapait aussi les saisies illisibles), donc le geste
                                    // normal « effacer puis retaper » détruisait le réglage.
                                    value={angleDraft !== null ? angleDraft : angle}
                                    onChange={(e) => {
                                        const brut = e.target.value;
                                        setAngleDraft(brut);
                                        const saisi = Number(brut);
                                        if (brut.trim() === '' || !Number.isFinite(saisi)) {
                                            // On garde l'angle précédent et on attend la suite
                                            // de la frappe, plutôt que de retomber à 0°.
                                            setAngleMsg(`Angle non renseigné : ${angle}° est conservé. Saisissez un nombre de degrés.`);
                                            return;
                                        }
                                        setAngleMsg('');
                                        onChangeGradient({ type: gradType, angle: saisi, stops });
                                    }}
                                    onBlur={() => { setAngleDraft(null); setAngleMsg(''); }}
                                    className="w-12 bg-black/40 border border-white/10 rounded px-1.5 py-1 text-xs text-center font-mono outline-none"
                                />
                                <span className="text-white/40 text-[10px]">°</span>
                                <div className="flex flex-1 gap-1">
                                    {[0, 90, 180, 270].map(a => (
                                        <button
                                            key={a}
                                            onClick={() => { setAngleDraft(null); setAngleMsg(''); onChangeGradient({ type: gradType, angle: a, stops }); }}
                                            className={`flex-1 py-0.5 rounded text-[10px] border font-mono ${angle === a ? 'bg-white text-black font-bold border-white' : 'bg-black/20 text-white/40 border-white/10 hover:border-white hover:text-white'}`}
                                        >
                                            {a}°
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Le message ne vit que le temps de la saisie : cet éditeur
                                garde son état local d'un calque à l'autre, un message
                                laissé là parlerait du réglage d'un autre calque. */}
                            {angleDraft !== null && angleMsg && (
                                <p className="text-[9px] text-red-400/90 font-mono mt-1.5 leading-snug">{angleMsg}</p>
                            )}
                        </div>
                    )}

                    {/* Hue Shift — roue de teinte globale */}
                    <div className="pt-2 border-t border-white/5">
                        <label className="block text-[10px] text-white/40 font-mono mb-1.5 uppercase">Teinte globale</label>
                        <div className="flex gap-2 items-center">
                            <div className="flex-1 relative h-3 rounded-full overflow-hidden" style={{
                                background: 'linear-gradient(90deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                            }}>
                                <input
                                    type="range" min="-180" max="180" value={hueShift}
                                    onChange={(e) => applyHueShift(Number(e.target.value))}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="absolute top-0 h-full w-0.5 bg-white shadow-md pointer-events-none" style={{ left: `${(hueShift + 180) / 360 * 100}%` }} />
                            </div>
                            <span className="text-[9px] font-mono text-white/50 w-8 text-right">{hueShift}°</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Champ numérique de l'inspecteur (boutons − / + et saisie libre).
//
// Il vit dans son propre composant pour tenir deux états locaux : le texte en
// cours de frappe, et le message affiché quand la valeur sort des bornes. Avant,
// `min` et `max` n'étaient posés que sur l'`<input type="number">` : le
// navigateur ne les fait respecter NI à la saisie NI aux boutons, si bien que les
// bornes annoncées n'existaient qu'à l'affichage. Un corps de 900 pt, une
// position négative ou une échelle de code-barres à 400 partaient tels quels dans
// le calque, sans un mot.
const NumberField = ({ label, value, min, max, step = 1, mixed = false, suffix = '', onApply }) => {
    // Tant que le champ est édité, il montre la frappe et non la valeur du
    // calque : sinon vider le champ pour retaper le remplissait aussitôt.
    const [brouillon, setBrouillon] = useState(null);
    const [message, setMessage] = useState('');

    const fr = (n) => String(n).replace('.', ',');
    const bornesTexte = Number.isFinite(min) && Number.isFinite(max)
        ? `entre ${fr(min)} et ${fr(max)}`
        : Number.isFinite(min) ? `supérieur ou égal à ${fr(min)}`
        : Number.isFinite(max) ? `inférieur ou égal à ${fr(max)}`
        : null;

    const arrondi = (n) => Math.round(n * 100) / 100;
    const ecreter = (n) => {
        let v = n;
        if (Number.isFinite(min)) v = Math.max(min, v);
        if (Number.isFinite(max)) v = Math.min(max, v);
        return arrondi(v);
    };

    const appliquer = (n) => {
        const borne = ecreter(n);
        setMessage(borne !== arrondi(n) && bornesTexte
            ? `Valeur hors limites : saisissez un nombre ${bornesTexte}. ${fr(borne)} a été appliqué.`
            : '');
        onApply(borne);
    };

    // − / + : pas = `step` ; ×10 avec Maj, ×100 avec Maj+Alt (comme les flèches
    // clavier du canvas). Arrondi à 2 décimales pour éviter la dérive flottante.
    // Le brouillon est abandonné : les boutons repartent de la valeur du calque, et
    // le champ doit se remettre à l'afficher. Sans cette remise à zéro, un champ
    // vidé restait vide et rouge pendant que les clics sur − / + modifiaient bien
    // le calque : les boutons paraissaient morts.
    const bump = (e, dir) => {
        const mult = (e.shiftKey && e.altKey) ? 100 : e.shiftKey ? 10 : 1;
        const depart = Number(value);
        const base = Number.isFinite(depart) ? depart : (Number.isFinite(min) ? min : 0);
        setBrouillon(null);
        appliquer(base + dir * step * mult);
    };

    const affiche = mixed ? ''
        : brouillon !== null ? brouillon
        : Number.isFinite(Number(value)) ? arrondi(Number(value))
        : '';

    const btn = "flex-none px-3 text-white/50 py-1 hover:bg-white/10 hover:text-white font-bold text-sm transition-colors";
    return (
        <div className="group/input">
            {label && <label className="text-[9px] text-white/50 block mb-1.5 font-mono uppercase tracking-widest">{label}</label>}
            <div className={`flex bg-black/30 border rounded-lg overflow-hidden shadow-inner transition-colors ${mixed ? 'border-forge-mauve/40' : 'border-white/5 group-hover/input:border-white/10'}`}>
                <button title="Maj = ×10 · Maj+Alt = ×100" onClick={(e) => bump(e, -1)} className={btn}>-</button>
                <div className="relative flex-1 flex items-center">
                    <input
                        type="number"
                        value={affiche}
                        placeholder={mixed ? '—' : ''}
                        title={bornesTexte ? `Valeur admise : nombre ${bornesTexte}` : ''}
                        onChange={e => {
                            const brut = e.target.value;
                            setBrouillon(brut);
                            const saisi = Number(brut);
                            if (brut.trim() === '' || !Number.isFinite(saisi)) {
                                // Un champ vidé valait `Number('') === 0`, c'est-à-dire une
                                // largeur ou un corps remis à zéro dès la première touche
                                // effacée. On garde le réglage et on attend la suite.
                                setMessage('Valeur non numérique : saisissez un nombre, le réglage précédent est conservé.');
                                return;
                            }
                            appliquer(saisi);
                        }}
                        // Le message part avec le brouillon : il ne parle que de la
                        // frappe qu'on vient d'abandonner. Laissé en place, il restait
                        // affiché en rouge sous une valeur redevenue correcte, sans
                        // aucun moyen de le faire disparaître.
                        onBlur={() => { setBrouillon(null); setMessage(''); }}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={`w-full text-center bg-transparent px-1 py-1 text-xs outline-none font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${mixed ? 'placeholder:text-forge-mauve placeholder:font-bold text-forge-mauve' : 'text-white'} ${suffix ? 'pr-3' : ''}`}
                        min={min} max={max} step="any"
                    />
                    {suffix && !mixed && (
                        <span className="absolute right-1 text-[10px] text-white/70 font-mono pointer-events-none select-none">
                            {suffix}
                        </span>
                    )}
                </div>
                <button title="Maj = ×10 · Maj+Alt = ×100" onClick={(e) => bump(e, +1)} className={btn}>+</button>
            </div>
            {message && (
                <p className="text-[9px] text-red-400/90 font-mono mt-1 leading-snug">{message}</p>
            )}
        </div>
    );
};

/**
 * Les six boutons d'alignement. Le même bloc sert aux deux cas, qui ne diffèrent
 * que par leur RÉFÉRENCE (cf. alignElements dans le store) : à plusieurs, les
 * éléments s'alignent entre eux ; à un seul, sur le cadre de l'étiquette. Le
 * libellé le dit, sans quoi le même bouton semblerait ne rien faire selon la
 * sélection.
 */
function BoutonsAlignement({ ids, surEtiquette }) {
    const alignElements = useLabelStore((s) => s.alignElements);
    return (
        <div>
            <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-2">
                {surEtiquette ? "Aligner sur l'étiquette" : 'Aligner'}
            </label>
            <div className="grid grid-cols-3 gap-1 bg-black/30 p-1 rounded-lg border border-white/5 shadow-inner">
                {[
                { dir: 'left', label: 'Gauche', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="3" x2="4" y2="21"/><rect x="8" y="5" width="12" height="4" rx="1"/><rect x="8" y="13" width="8" height="4" rx="1"/></svg> },
                { dir: 'center', label: 'Centre H', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="3" x2="12" y2="21" strokeDasharray="2 2"/><rect x="6" y="5" width="12" height="4" rx="1"/><rect x="8" y="13" width="8" height="4" rx="1"/></svg> },
                { dir: 'right', label: 'Droite', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="20" y1="3" x2="20" y2="21"/><rect x="4" y="5" width="12" height="4" rx="1"/><rect x="8" y="13" width="8" height="4" rx="1"/></svg> },
                { dir: 'top', label: 'Haut', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="4" x2="21" y2="4"/><rect x="5" y="8" width="4" height="12" rx="1"/><rect x="13" y="8" width="4" height="8" rx="1"/></svg> },
                { dir: 'middle', label: 'Centre V', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12" strokeDasharray="2 2"/><rect x="5" y="6" width="4" height="12" rx="1"/><rect x="13" y="8" width="4" height="8" rx="1"/></svg> },
                { dir: 'bottom', label: 'Bas', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="20" x2="21" y2="20"/><rect x="5" y="4" width="4" height="12" rx="1"/><rect x="13" y="8" width="4" height="8" rx="1"/></svg> },
                ].map(({ dir, label, icon }) => (
                <button
                    key={dir}
                    onClick={() => { alignElements(ids, dir); setTimeout(() => useLabelStore.getState()._pushState(`Aligner ${label}`), 0); }}
                    className="flex items-center justify-center py-2 rounded-md text-white/40 hover:bg-white/10 hover:text-white transition-all"
                    title={surEtiquette ? `Aligner ${label} sur l'étiquette` : `Aligner ${label}`}
                    aria-label={surEtiquette ? `Aligner ${label} sur l'étiquette` : `Aligner ${label}`}
                >
                    {icon}
                </button>
                ))}
            </div>
        </div>
    );
}

export default function Inspector() {
    const {
        selectedElementIds, elements, canvas, setCanvas,
        updateElement, updateElements, removeElements, loadProject, addElement,
        reorderElement, linkElements, unlinkElement, selectElement, getProjectData,
        alignElements, distributeElements
    } = useLabelStore();

    const [rightWidth, setRightWidth] = React.useState(320); // max-w-600 min-w-200
    const isResizingRight = React.useRef(false);

    React.useEffect(() => {
        const handleMouseMove = (e) => {
            if (isResizingRight.current) {
                setRightWidth(Math.min(Math.max(200, document.body.clientWidth - e.clientX), 600));
            }
        };
        const handleMouseUp = () => {
            if (isResizingRight.current) {
                isResizingRight.current = false;
                document.body.style.cursor = 'default';
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const hasSelection = selectedElementIds.length > 0;
    const isMultiSelection = selectedElementIds.length > 1;
    const el = selectedElementIds.length === 1 ? elements.find(e => e.id === selectedElementIds[0]) : null;

    // Cinq réglages de l'inspecteur ont un JUMEAU dans le rich text : la barre
    // flottante d'édition (double-clic) pose des marks qui, au rendu, GAGNENT
    // sur la propriété à plat (cf. utils/richTextModel). Sans le nettoyage
    // ci-dessous, choisir une couleur ici ne changeait rien à l'écran sur un
    // calque déjà édité — alors que l'infobulle promet d'« uniformiser toute la
    // sélection ». Le geste écrit donc le plat ET retire la mark correspondante,
    // en une seule mutation (un seul re-rendu, un seul pas d'historique).
    const CHAMPS_A_JUMEAU = ['color', 'font', 'size', 'fontWeight', 'fontStyle'];
    const MARK_DU_CHAMP = { color: 'color', font: 'font', size: 'size', fontWeight: 'bold', fontStyle: 'italic' };

    const updateSingle = (field, value) => {
        const partiel = { [field]: value };
        if (el.type === 'text' && el.content && CHAMPS_A_JUMEAU.includes(field)) {
            // Le gras et l'italique se RETIRENT quand on les repose : une mark
            // `bold` laissée en place rendrait impossible de dégraisser le calque.
            const nettoye = retirerMarksDeStyle(el.content, [MARK_DU_CHAMP[field]]);
            if (nettoye !== el.content) partiel.content = nettoye;
        }
        updateElement(el.id, partiel);
    };

    // Longueur de saisie imposée par les symbologies numériques (le dernier
    // chiffre, de contrôle, est calculé) — absente pour les symbologies libres.
    const BARCODE_FIXED_LENGTH = { ean13: 12, ean8: 7, upca: 11 };

    // Avant, changer de symbologie laissait `el.text` intact : la nouvelle grille
    // tronquait à sa longueur, et la première frappe écrivait la version tronquée
    // dans le calque. Passer d'EAN-13 à EAN-8 détruisait donc les chiffres 8 à 12
    // sans un mot, et revenir à EAN-13 ne les retrouvait pas. La saisie complète
    // est désormais conservée dans `el.textFull` : `el.text` ne porte que ce que
    // la symbologie courante sait afficher, la queue attend en réserve.
    const fullBarcodeText = () => {
        const visible = el.text || '';
        const reserve = el.textFull || '';
        // La réserve ne sert qu'à PROLONGER le texte visible. Si elle ne le
        // préfixe pas, c'est qu'un autre chemin a réécrit le code : on ne
        // ressuscite rien, la saisie en cours a toujours raison.
        return reserve.length > visible.length && reserve.startsWith(visible) ? reserve : visible;
    };

    const changeSymbology = (symbology) => {
        const full = fullBarcodeText();
        const length = BARCODE_FIXED_LENGTH[symbology];
        updateElement(el.id, {
            symbology,
            text: trimTrailingBlanks(length ? full.slice(0, length) : full),
            textFull: full,
        });
    };

    // La grille n'édite que sa fenêtre de `length` caractères ; on la ramène à
    // cette longueur avant de recoller la queue, sinon un chiffre effacé en fin
    // de fenêtre décalerait toute la réserve d'un cran.
    const updateBarcodeDigits = (value, length) => {
        const tail = fullBarcodeText().slice(length);
        updateElement(el.id, {
            text: value,
            textFull: tail ? value.padEnd(length, ' ') + tail : value,
        });
    };

    // Pousse une entrée d'historique APRÈS une édition inspecteur, en regroupant
    // les modifications rapprochées (debounce) → un undo annule l'ajustement
    // entier. Sans ça, les éditions via l'inspecteur ne créaient AUCUN historique
    // (seul le drag/clavier sur le canvas en créait).
    const _histTimer = useRef(null);
    const commitHistory = (name) => {
        if (_histTimer.current) clearTimeout(_histTimer.current);
        _histTimer.current = setTimeout(() => {
            useLabelStore.getState()._pushState(typeof name === 'string' ? name : 'Inspecteur');
        }, 350);
    };

    // Analyse du rich text content : détecte les valeurs mixtes (multi-typo /
    // multi-taille / gras partiel...) pour griser les contrôles concernés
    // dans l'inspecteur, comme Photoshop/Illustrator.
    const contentAnalysis = React.useMemo(() => {
        if (!el || el.type !== 'text' || !el.content) return null;
        return analyzeContent(el.content);
    }, [el?.id, el?.content]);
    // Ce que le calque montre VRAIMENT : une couleur ou une police posée au
    // double-clic vit dans les marks et l'emporte au rendu. Les contrôles
    // affichaient jusqu'ici `el.color` / `el.font` / `el.size`, c'est-à-dire
    // précisément la valeur qui n'est PAS à l'écran : pastille noire sur un
    // texte rouge, et l'utilisateur en concluait que le calque était noir.
    const styleReel = React.useMemo(
        () => (el && el.type === 'text' ? styleEffectifTexte(el) : null),
        [el?.id, el?.content, el?.color, el?.font, el?.size]
    );
    const isMixed = (key) => {
        if (!contentAnalysis) return false;
        const a = contentAnalysis[key];
        if (typeof a === 'string') return a === 'mixed';
        return a?.state === 'mixed';
    };
    // Classe Tailwind à appliquer sur un wrapper de contrôle "mixte" — visuel
    // grisé + tooltip d'info, mais l'utilisateur peut toujours cliquer pour
    // appliquer une valeur uniforme à tout le calque.
    const mixedClass = (key) => isMixed(key)
        ? 'opacity-40 [&_*]:cursor-help'
        : '';

    // Helper pour générer un input numérique avec boutons +/- plus larges
    // Argument optionnel `mixed` : affiche un placeholder '—' au lieu de la valeur
    // pour signaler que la sélection a des valeurs différentes (style Photoshop).
    // Argument optionnel `suffix` : affiche une unité (ex: '°') accolée à la valeur,
    // sans casser le type=number (overlay absolute non-interactif).
    const renderNumberInput = (label, field, val, min, max, step = 1, mixed = false, suffix = '') => {
        const histName = typeof label === 'string' && label ? label : field;
        // La clé porte l'identifiant du calque : sans elle, changer de sélection
        // réutiliserait l'état local du champ précédent, et son message d'erreur
        // resterait affiché sous la valeur d'un autre calque.
        return (
            <NumberField
                key={`${el.id}:${field}`}
                label={label}
                value={val}
                min={min}
                max={max}
                step={step}
                mixed={mixed}
                suffix={suffix}
                onApply={(next) => { updateSingle(field, next); commitHistory(histName); }}
            />
        );
    };

    return (
        <div
            className="bg-black/40 backdrop-blur-xl border-l border-white/5 overflow-y-auto overflow-x-hidden flex flex-col shrink-0 relative hover:group/aside shadow-2xl transition-all"
            style={{ width: `${rightWidth}px` }}
        >
            {/* Resize Handle Inspecteur (Bord Gauche) */}
            <div
                className="absolute left-0 top-0 bottom-0 w-[6px] cursor-col-resize z-50 hover:bg-white/50 transition-colors"
                style={{ transform: 'translateX(-50%)' }}
                onMouseDown={(e) => { e.preventDefault(); isResizingRight.current = true; document.body.style.cursor = 'col-resize'; }}
            />

            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-2 mt-2">
                    <h2 className="text-white font-mono text-sm uppercase tracking-wider">Inspecteur</h2>
                    <span className="text-[10px] bg-black/20 px-2 py-1 rounded text-white/40 font-mono">
                        {selectedElementIds.length} Sél.
                    </span>
                </div>

                {(!hasSelection) && (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-6 border border-dashed border-white/10 bg-black/10 rounded-2xl opacity-60 shadow-inner m-2">
                        {/* Icône dessinée en SVG comme partout ailleurs : un emoji ne
                            suit ni la couleur ni la taille du texte, et son dessin
                            change d'un poste à l'autre. */}
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="mb-3 text-white/50 drop-shadow-lg">
                            <path d="m4 4 7.07 17 2.51-7.39L21 11.07z" />
                        </svg>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-white/50">Sélectionnez un calque</p>
                    </div>
                )}

                {/* Un tracé de découpe a son propre panneau : les réglages génériques
                    d'une forme (couleur libre, contour, rotation, taille) le casseraient. */}
                {hasSelection && !isMultiSelection && el && estDecoupe(el) && (
                    <PanneauDecoupe el={el} ChampNombre={NumberField} onHistorique={commitHistory} />
                )}

                {hasSelection && !isMultiSelection && el && !estDecoupe(el) && (
                    <div className="space-y-4 flex-1 mb-6">
                        <div className="flex justify-between items-center pb-3 mb-4 border-b border-white/5">
                            <h3 className="text-white text-[10px] tracking-widest font-mono uppercase font-bold drop-shadow-md">Propriétés {el.name ? `(${el.name})` : `(${LIBELLES_TYPE[el.type] || el.type})`}</h3>
                        </div>

                        {/* BANDEAU CALQUE RÉGLEMENTAIRE — affiché en tête si el.regBlock défini */}
                        <RegulatoryInspectorBanner el={el} />

                        {/* AJUSTER LE BLOC AU CONTENU — façon InDesign (Objet →
                            Ajustement) : resserre le cadre sur le contenu réel.
                            Raccourci : Cmd/Ctrl+Alt+F. */}
                        {canFitFrame(el) && (
                            <button
                                onClick={() => fitSelectionToContent(useLabelStore.getState())}
                                title="Resserre le cadre sur le contenu réel (texte, image, code-barres) — Cmd/Ctrl+Alt+F"
                                className="w-full mb-4 py-2 rounded-lg text-[9px] font-mono font-bold uppercase tracking-widest flex items-center justify-center gap-2 bg-[rgba(var(--forge-accent),0.10)] text-[rgba(var(--forge-accent),1)] border border-[rgba(var(--forge-accent),0.3)] hover:bg-[rgba(var(--forge-accent),0.2)] transition-all"
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                                    <rect x="9" y="9" width="6" height="6" rx="1" />
                                </svg>
                                Ajuster au contenu
                            </button>
                        )}

                        {/* TYPE DE CODE-BARRES */}
                        {el.type === 'barcode' && (
                            <div className="mb-4">
                                <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Symbologie (Format)</label>
                                <select value={el.symbology || 'ean13'} onChange={(e) => changeSymbology(e.target.value)} className="w-full bg-black/30 border border-white/5 shadow-inner hover:border-white/10 focus:border-white/20 rounded-lg px-3 py-2 text-sm outline-none text-white transition-colors cursor-pointer appearance-none">
                                    {SYMBOLOGIES.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
                                </select>
                            </div>
                        )}

                        {/* DONNÉES CODE-BARRES — la saisie de texte des calques 'text'
                            se fait directement sur le calque (double-clic). On a retiré
                            le RichTextEditor de l'inspecteur qui faisait doublon. */}
                        {el.type === 'barcode' && (
                            <div className="mb-4">
                                <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1">
                                    Saisissez votre code à reproduire
                                </label>
                                <p className="text-[9px] text-white/40 font-mono mb-2 leading-snug">
                                    Tapez les chiffres / caractères de votre code-barres existant pour le reproduire à l'identique.
                                </p>
                                <div>
                                    {BARCODE_FIXED_LENGTH[el.symbology] ? (
                                        <BarcodeDigitInputs
                                            value={el.text}
                                            length={BARCODE_FIXED_LENGTH[el.symbology]}
                                            symbology={el.symbology}
                                            onChange={(val) => updateBarcodeDigits(val, BARCODE_FIXED_LENGTH[el.symbology])}
                                        />
                                    ) : (
                                        // Symbologie libre : pas de troncature, donc rien à
                                        // mettre en réserve — on aligne `textFull` sur la
                                        // saisie pour qu'un retour vers EAN ne ressuscite
                                        // pas un code effacé volontairement ici.
                                        <input type="text" value={el.text} onChange={(e) => updateElement(el.id, { text: e.target.value, textFull: e.target.value })} className="w-full bg-black/30 border border-white/5 shadow-inner hover:border-white/10 focus:border-white/20 rounded-lg px-3 py-2 text-sm outline-none font-mono text-white transition-colors" placeholder="Tapez ici votre code" />
                                    )}
                                    <label className="flex items-center gap-2 cursor-pointer mt-3 pb-1">
                                        <input type="checkbox" checked={el.showText !== false} onChange={e => updateSingle('showText', e.target.checked)} className="accent-white w-3 h-3" />
                                        <span className="text-[10px] font-mono text-white/50 tracking-widest uppercase">Afficher le texte sous les barres</span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* POLICE */}
                        {(el.type === 'text' || el.type === 'barcode') && (
                            <div className="space-y-3 mb-4">
                                <div style={{ zIndex: 50, position: 'relative' }}
                                     className={isMixed('fontFamily') ? 'ring-1 ring-forge-mauve/40 rounded-lg p-0.5 -m-0.5' : ''}
                                     title={isMixed('fontFamily') ? 'Multi-polices — éditez chaque mot via la barre du calque, ou choisissez une police pour tout uniformiser' : ''}>
                                    <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">
                                        Typographie {isMixed('fontFamily') && <span className="text-forge-mauve/80 font-bold">· multiple</span>}
                                    </label>
                                    <FontPicker
                                        currentFont={isMixed('fontFamily') ? '— multiple —' : ((styleReel && styleReel.font) || el.font)}
                                        onChange={(f) => updateSingle('font', f)}
                                    />
                                </div>
                                {el.type === 'text' && (
                                    <div className="flex gap-2 bg-black/30 p-1 border border-white/5 shadow-inner rounded-lg">
                                        <button
                                            onClick={() => updateSingle('fontWeight', el.fontWeight === 'bold' ? 'normal' : 'bold')}
                                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
                                                isMixed('bold')
                                                    ? 'bg-forge-mauve/20 text-forge-mauve border border-forge-mauve/40'
                                                    : el.fontWeight === 'bold'
                                                        ? 'bg-white text-black shadow-md'
                                                        : 'text-white/50 hover:bg-white/10 hover:text-white'
                                            }`}
                                            title={isMixed('bold') ? 'Gras partiel — cliquez pour appliquer ou retirer le gras sur tout le texte' : 'Gras'} aria-label={isMixed('bold') ? 'Gras partiel — cliquez pour appliquer ou retirer le gras sur tout le texte' : 'Gras'}
                                        >
                                            {isMixed('bold') ? '—' : 'B'}
                                        </button>
                                        <button
                                            onClick={() => updateSingle('fontStyle', el.fontStyle === 'italic' ? 'normal' : 'italic')}
                                            className={`flex-1 py-1.5 text-xs italic rounded-md font-serif transition-all ${
                                                isMixed('italic')
                                                    ? 'bg-forge-mauve/20 text-forge-mauve border border-forge-mauve/40 not-italic font-bold'
                                                    : el.fontStyle === 'italic'
                                                        ? 'bg-white text-black shadow-md'
                                                        : 'text-white/50 hover:bg-white/10 hover:text-white'
                                            }`}
                                            title={isMixed('italic') ? 'Italique partiel — cliquez pour appliquer ou retirer l\'italique sur tout le texte' : 'Italique'} aria-label={isMixed('italic') ? 'Italique partiel — cliquez pour appliquer ou retirer l\'italique sur tout le texte' : 'Italique'}
                                        >
                                            {isMixed('italic') ? '—' : 'I'}
                                        </button>
                                        <button
                                            onClick={() => updateSingle('textTransform', el.textTransform === 'uppercase' ? 'none' : 'uppercase')}
                                            className={`flex-1 py-1.5 text-[10px] rounded-md font-mono transition-all ${el.textTransform === 'uppercase' ? 'bg-white text-black font-bold shadow-md' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                                            title="Tout en majuscules" aria-label="Tout en majuscules"
                                        >
                                            TT
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* GRILLE D'ATTRIBUTS VISUELS (Typo Avancée) */}
                        {el.type === 'text' && (
                            <div className="grid grid-cols-2 gap-3 pb-4 border-b border-white/5 mb-4">
                                {renderNumberInput('Approche (px)', 'letterSpacing', el.letterSpacing || 0, -50, 200, 0.1)}
                                {renderNumberInput('Interligne (em)', 'lineHeight', el.lineHeight !== undefined ? el.lineHeight : 1.2, 0.1, 10, 0.1)}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            {['text', 'barcode'].includes(el.type) && (
                                <div title={isMixed('fontSize') ? 'Tailles multiples — cliquez sur - ou +, ou saisissez une valeur, pour uniformiser toute la sélection' : ''}>
                                    {renderNumberInput(
                                        <>
                                            Corps (Pt) {isMixed('fontSize') && <span className="text-forge-mauve/80 font-bold">· multiple</span>}
                                        </>,
                                        'size', (styleReel && styleReel.size) || el.size || 12, 0.5, 500, 0.5,
                                        isMixed('fontSize')
                                    )}
                                </div>
                            )}
                            {el.type !== 'image' && (
                                <div className="col-span-2 mb-2 relative"
                                     title={el.type === 'text' && isMixed('color') ? 'Couleurs multiples — choisissez une couleur pour uniformiser toute la sélection' : ''}>
                                    <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">
                                        Remplissage {el.type === 'text' && isMixed('color') && <span className="text-forge-mauve/80 font-bold">· multiple</span>}
                                    </label>
                                    <div className={el.type === 'text' && isMixed('color') ? 'ring-1 ring-forge-mauve/40 rounded-2xl' : ''}>
                                        <GradientEditor
                                            fillType={el.fillType || 'solid'}
                                            gradient={el.gradient}
                                            color={(styleReel && styleReel.color) || el.color}
                                            // Le rendu d'un code-barres ne lit que `el.color` :
                                            // le mode dégradé s'affichait et se réglait pour rien.
                                            allowGradient={el.type !== 'barcode'}
                                            onChangeFillType={(t) => updateSingle('fillType', t)}
                                            onChangeColor={(c) => updateSingle('color', c)}
                                            onChangeGradient={(g) => updateSingle('gradient', g)}
                                        />
                                    </div>
                                    {el.type === 'text' && isMixed('color') && (
                                        <div
                                            className="mt-2 h-2 rounded-full border border-black/30"
                                            style={{ background: 'linear-gradient(90deg, #ef4444, #f59e0b, #eab308, #84cc16, #22c55e, #06b6d4, #3b82f6, #a855f7)' }}
                                            title="Plusieurs couleurs dans le texte"
                                        />
                                    )}
                                </div>
                            )}

                            {/* CONTOUR — pas sur un code-barres : son rendu n'utilise
                                aucune des propriétés de contour, si bien que cocher la
                                case et régler l'épaisseur ne produisait rien, ni à
                                l'écran ni à l'export. */}
                            {el.type !== 'image' && el.type !== 'barcode' && (
                                <div className="col-span-2 pt-4 border-t border-white/5 mt-2">
                                    <div className="flex items-center gap-2 mb-3">
                                        <input type="checkbox" checked={el.strokeEnabled || false} onChange={e => updateSingle('strokeEnabled', e.target.checked)} className="accent-white cursor-pointer w-3 h-3" id="enableStroke"/>
                                        <label htmlFor="enableStroke" className="text-[10px] text-white/70 font-mono uppercase tracking-widest cursor-pointer select-none">Contour</label>
                                    </div>
                                    {el.strokeEnabled && (
                                        <div className="bg-black/30 p-3 rounded-xl border border-white/5 shadow-inner">
                                            <div className="mb-3">
                                                {renderNumberInput('Épaisseur (px)', 'strokeWidth', el.strokeWidth !== undefined ? el.strokeWidth : 2, 0.1, 100, 0.1)}
                                            </div>
                                            <GradientEditor 
                                                fillType={el.strokeType || 'solid'}
                                                gradient={el.strokeGradient}
                                                color={el.strokeColor || '#FFFFFF'}
                                                onChangeFillType={(t) => updateSingle('strokeType', t)}
                                                onChangeColor={(c) => updateSingle('strokeColor', c)}
                                                onChangeGradient={(g) => updateSingle('strokeGradient', g)}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            {el.type === 'barcode' && (
                                <div className="col-span-2 mt-2">
                                    <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Fond Code-barres</label>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <ChampCouleur
                                            couleur={el.bgColor || '#FFFFFF'}
                                            onChange={(hex) => updateSingle('bgColor', hex)}
                                            titre="Fond du code-barres"
                                        />
                                        <button onClick={() => updateSingle('bgColor', 'transparent')} className="text-[9px] text-white/30 hover:text-red-400 font-mono underline ml-1 transition-colors uppercase tracking-widest px-2 py-1">Rendre transparent</button>
                                    </div>
                                </div>
                            )}
                            {el.type === 'shape' && el.shapeType === 'rounded-rect' && (
                                <div className="col-span-2">
                                    {renderNumberInput('Rayon Arrondi (px)', 'borderRadius', el.borderRadius || 0, 0, Math.min(el.width, el.height) / 2 || 100, 1)}
                                </div>
                            )}
                        </div>

                        {/* ATTRIBUTS AVANCES CODE-BARRES */}
                        {el.type === 'barcode' && (
                            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10 mt-4">
                                {renderNumberInput('Échelle (Pixels)', 'bcScale', el.bcScale || 3, 1, 10, 1)}
                                {!SYMBOLOGIES_CARREES.has(el.symbology) && renderNumberInput('Hauteur Barres', 'bcHeight', el.bcHeight || 15, 5, 100, 1)}
                                {renderNumberInput('Marge', 'bcMargin', el.bcMargin || 0, 0, 50, 1)}
                                {renderNumberInput('Réd. Barres (BWR)', 'bcInkSpread', el.bcInkSpread || 0, 0, 5, 0.1)}
                            </div>
                        )}

                        {/* OPACITE */}
                        <div className={`mb-5 ${el.locked ? 'opacity-50 pointer-events-none' : ''}`}>
                            <label className="flex justify-between text-[9px] text-white/50 uppercase tracking-widest font-mono mb-2">
                                <span>Opacité</span>
                                <span className="font-bold text-white/80">{el.opacity !== undefined ? el.opacity : 100}%</span>
                            </label>
                            <input 
                                type="range"
                                min="0"
                                max="100"
                                value={el.opacity !== undefined ? el.opacity : 100}
                                onChange={(e) => updateSingle('opacity', Number(e.target.value))}
                                className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer outline-none shadow-inner"
                                // Le blanc du rail rempli s'écrivait `rgba(var(255,255,255), 1)`.
                                // `var()` attend un nom de propriété personnalisée, pas des
                                // composantes : la couleur était invalide, donc le navigateur
                                // rejetait TOUTE la déclaration `background` et le curseur
                                // paraissait uniformément gris, sans jamais montrer la part remplie.
                                style={{ background: `linear-gradient(to right, rgba(255,255,255,1) ${(el.opacity !== undefined ? el.opacity : 100)}%, rgba(255,255,255,0.1) ${(el.opacity !== undefined ? el.opacity : 100)}%)` }}
                            />
                        </div>

                        {/* TRAIT / LIGNE (lignes uniquement) */}
                        {el.type === 'shape' && el.shapeType === 'line' && (
                            <div className="mb-4 space-y-3">
                                {renderNumberInput('Épaisseur du trait', 'lineThickness', el.lineThickness !== undefined ? el.lineThickness : 0.15, 0.01, 20, 0.05)}

                                {/* Bord gauche */}
                                <div>
                                    <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Extrémité gauche</label>
                                    <div className="flex gap-1 bg-black/30 p-1 rounded-lg border border-white/5 shadow-inner">
                                        {[
                                            { value: 'square', label: 'Carré', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="7" width="10" height="10" /><line x1="14" y1="12" x2="22" y2="12" strokeWidth="2"/></svg> },
                                            { value: 'round', label: 'Rond', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="12" r="5" /><line x1="13" y1="12" x2="22" y2="12" strokeWidth="2"/></svg> },
                                            { value: 'arrow', label: 'Flèche', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="12" x2="20" y2="12"/><polyline points="10 7 4 12 10 17"/></svg> },
                                        ].map(cap => (
                                            <button key={cap.value} onClick={() => updateSingle('lineCapLeft', cap.value)} title={cap.label} aria-label={cap.label}
                                                className={`flex-1 flex justify-center items-center py-1.5 rounded-md text-xs transition-all ${(el.lineCapLeft || 'square') === cap.value ? 'bg-white text-black shadow-md' : 'text-white/40 hover:text-white hover:bg-white/10'}`}>
                                                {cap.icon}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Bord droit */}
                                <div>
                                    <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Extrémité droite</label>
                                    <div className="flex gap-1 bg-black/30 p-1 rounded-lg border border-white/5 shadow-inner">
                                        {[
                                            { value: 'square', label: 'Carré', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="10" y2="12" strokeWidth="2"/><rect x="10" y="7" width="10" height="10" /></svg> },
                                            { value: 'round', label: 'Rond', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="11" y2="12" strokeWidth="2"/><circle cx="16" cy="12" r="5" /></svg> },
                                            { value: 'arrow', label: 'Flèche', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="12" x2="20" y2="12"/><polyline points="14 7 20 12 14 17"/></svg> },
                                        ].map(cap => (
                                            <button key={cap.value} onClick={() => updateSingle('lineCapRight', cap.value)} title={cap.label} aria-label={cap.label}
                                                className={`flex-1 flex justify-center items-center py-1.5 rounded-md text-xs transition-all ${(el.lineCapRight || 'square') === cap.value ? 'bg-white text-black shadow-md' : 'text-white/40 hover:text-white hover:bg-white/10'}`}>
                                                {cap.icon}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Taille de la flèche (visible si au moins un côté est une flèche) */}
                                {((el.lineCapLeft === 'arrow') || (el.lineCapRight === 'arrow')) && (
                                    <div>
                                        {renderNumberInput('Taille de la flèche', 'lineArrowSize', el.lineArrowSize !== undefined ? el.lineArrowSize : 2, 0.5, 10, 0.5)}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* OMBRE PORTÉE */}
                        <div className="mb-5">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[9px] text-white/50 uppercase tracking-widest font-mono">Ombre portée</label>
                                <button
                                    onClick={() => { updateSingle('shadowEnabled', !el.shadowEnabled); setTimeout(() => useLabelStore.getState()._pushState('Ombre'), 0); }}
                                    className={`w-8 h-4 rounded-full transition-all duration-200 ${el.shadowEnabled ? 'bg-amber-500' : 'bg-white/10'} relative`}
                                >
                                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${el.shadowEnabled ? 'left-4' : 'left-0.5'}`} />
                                </button>
                            </div>
                            {el.shadowEnabled && (
                                <div className="space-y-3 p-3 bg-black/20 rounded-xl border border-white/5">
                                    {/* Couleur + Opacité */}
                                    {/* Mode: Externe / Interne.
                                        L'ombre interne est peinte par un recouvrement rectangulaire
                                        (arrondi ou circulaire selon la forme, cf. LabelCanvas) : elle
                                        ne sait pas suivre une silhouette quelconque. Sur un
                                        pictogramme vectoriel ou une ligne, elle dessinerait un cadre
                                        qui n'a rien à voir avec le dessin, là où l'ombre externe suit
                                        l'alpha. On ne propose donc pas le choix dans ces cas. */}
                                    {!(el.type === 'shape' && (el.svgPath || el.shapeType === 'line')) && (
                                    <div className="flex gap-1 bg-black/30 p-1 rounded-lg border border-white/5 mb-3">
                                        <button
                                            onClick={() => updateSingle('shadowInset', false)}
                                            className={`flex-1 py-1.5 rounded-md text-[9px] uppercase font-bold tracking-wider transition-all ${!el.shadowInset ? 'bg-white text-black shadow-md' : 'text-white/40 hover:bg-white/10 hover:text-white'}`}
                                        >
                                            Externe
                                        </button>
                                        <button
                                            onClick={() => updateSingle('shadowInset', true)}
                                            className={`flex-1 py-1.5 rounded-md text-[9px] uppercase font-bold tracking-wider transition-all ${el.shadowInset ? 'bg-white text-black shadow-md' : 'text-white/40 hover:bg-white/10 hover:text-white'}`}
                                        >
                                            Interne
                                        </button>
                                    </div>
                                    )}

                                    {/* Couleur + Opacite */}
                                    <div className="flex gap-2 items-center">
                                        <label className="text-[8px] text-white/40 uppercase font-mono w-16">Couleur</label>
                                        <ChampCouleur
                                            couleur={el.shadowColor || '#000000'}
                                            taille="compact"
                                            titre="Couleur de l'ombre"
                                            onChange={(hex) => updateSingle('shadowColor', hex)}
                                        />
                                        <div className="flex-1">
                                            <input
                                                type="range" min="0" max="100" step="1"
                                                value={el.shadowOpacity !== undefined ? el.shadowOpacity : 75}
                                                onChange={(e) => updateSingle('shadowOpacity', Number(e.target.value))}
                                                className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                        <input
                                            type="number" min="0" max="100" step="1"
                                            value={el.shadowOpacity !== undefined ? el.shadowOpacity : 75}
                                            onChange={(e) => updateSingle('shadowOpacity', Math.min(100, Math.max(0, Number(e.target.value))))}
                                            className="w-10 bg-black/40 border border-white/10 rounded text-[9px] text-center text-white font-mono py-0.5 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                    </div>
                                    {/* Shadow slider row helper */}
                                    {[
                                        { label: 'Angle', field: 'shadowAngle', min: -180, max: 180, step: 1, btnStep: 1, def: 120, decimals: 0 },
                                        { label: 'Distance', field: 'shadowDistance', min: 0, max: 50, step: 0.1, btnStep: 0.1, def: 5, decimals: 1 },
                                        { label: 'Diffusion', field: 'shadowSpread', min: 0, max: 100, step: 1, btnStep: 1, def: 0, decimals: 0 },
                                        { label: 'Taille', field: 'shadowSize', min: 0, max: 50, step: 0.1, btnStep: 0.1, def: 5, decimals: 1 },
                                    ].map(({ label, field, min, max, step, btnStep, def, unit, decimals }) => {
                                        const val = el[field] !== undefined ? el[field] : def;
                                        const clamp = (v) => Math.round(Math.min(max, Math.max(min, v)) * 10) / 10;
                                        return (
                                            <div key={field} className="flex gap-1.5 items-center">
                                                <label className="text-[8px] text-white/40 uppercase font-mono w-16 shrink-0">{label}</label>
                                                <button
                                                    onClick={() => updateSingle(field, clamp(val - btnStep))}
                                                    className="w-5 h-5 flex items-center justify-center rounded bg-black/40 border border-white/10 text-white/50 hover:text-white hover:bg-white/10 text-[10px] font-bold transition-colors shrink-0"
                                                >-</button>
                                                <input
                                                    type="range" min={min} max={max} step={step}
                                                    value={val}
                                                    onChange={(e) => updateSingle(field, Number(e.target.value))}
                                                    className="flex-1 h-1 bg-white/10 rounded-lg appearance-none cursor-pointer min-w-0"
                                                />
                                                <button
                                                    onClick={() => updateSingle(field, clamp(val + btnStep))}
                                                    className="w-5 h-5 flex items-center justify-center rounded bg-black/40 border border-white/10 text-white/50 hover:text-white hover:bg-white/10 text-[10px] font-bold transition-colors shrink-0"
                                                >+</button>
                                                <input
                                                    type="number" min={min} max={max} step={btnStep}
                                                    value={decimals > 0 ? val.toFixed(decimals) : val}
                                                    onChange={(e) => updateSingle(field, clamp(Number(e.target.value)))}
                                                    className="w-12 bg-black/40 border border-white/10 rounded text-[9px] text-center text-white font-mono py-0.5 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shrink-0"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ROTATION (Disabled if locked) */}
                        <div className="mb-4">
                            <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Rotation {el.locked && '(Verrouillée)'}</label>
                            <div className={`flex gap-2 items-end ${el.locked ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="w-24">
                                    {renderNumberInput('', 'rotation', el.rotation || 0, -360, 360, 1, false, '°')}
                                </div>
                                <div className="flex gap-1 flex-1 mb-[2px] bg-black/30 p-1 border border-white/5 shadow-inner rounded-lg">
                                    {[0, 90, 180, 270].map(angle => (
                                        <button
                                            key={angle}
                                            onClick={() => updateSingle('rotation', angle)}
                                            // Un calque neuf n'a pas de `rotation` posée : la
                                            // comparaison stricte contre `undefined` laissait le
                                            // bouton « 0° » éteint sur un calque pourtant droit,
                                            // alors que le champ à gauche affichait bien 0.
                                            className={`flex-1 py-1.5 rounded-md text-[9px] transition-all font-mono font-bold ${(el.rotation || 0) === angle ? 'bg-white text-black shadow-md' : 'text-white/40 hover:bg-white/10 hover:text-white'}`}
                                        >
                                            {angle}°
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* MIROIR (Images/SVG uniquement) */}
                        {(el.type === 'image' || el.type === 'svg') && (
                            <div className={`mb-4 mt-1 ${el.locked ? 'opacity-50 pointer-events-none' : ''}`}>
                                {/* Mention « (Verrouillée) » comme la Rotation juste au-dessus :
                                    le bloc était estompé et neutralisé sur un calque verrouillé,
                                    mais son étiquette ne le disait pas, si bien que les boutons
                                    semblaient simplement en panne. */}
                                <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Miroir {el.locked && '(Verrouillée)'}</label>
                                <div className="flex gap-2 bg-black/30 p-1 border border-white/5 shadow-inner rounded-lg">
                                    {/* Libellés entiers : « Horizon. » se lisait comme le mot
                                        « horizon » et non comme l'axe du retournement. */}
                                    <button onClick={() => updateSingle('scaleX', el.scaleX === -1 ? 1 : -1)} title="Miroir horizontal" aria-label="Miroir horizontal" className={`flex-1 py-1.5 flex justify-center items-center gap-1.5 rounded-md text-[10px] uppercase font-bold tracking-wider transition-all ${el.scaleX === -1 ? 'bg-white text-black shadow-md' : 'text-white/40 hover:bg-white/10 hover:text-white'}`}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20" /><path d="M3 7l5 5-5 5V7z" /><path d="M21 7l-5 5 5 5V7z" /></svg>
                                        Horizontal
                                    </button>
                                    <button onClick={() => updateSingle('scaleY', el.scaleY === -1 ? 1 : -1)} title="Miroir vertical" aria-label="Miroir vertical" className={`flex-1 py-1.5 flex justify-center items-center gap-1.5 rounded-md text-[10px] uppercase font-bold tracking-wider transition-all ${el.scaleY === -1 ? 'bg-white text-black shadow-md' : 'text-white/40 hover:bg-white/10 hover:text-white'}`}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h20" /><path d="M7 3l5 5 5-5H7z" /><path d="M7 21l5-5 5 5H7z" /></svg>
                                        Vertical
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ALIGNEMENT TEXTE */}
                        {el.type === 'text' && (
                            <div className="mb-4">
                                <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5">Alignement</label>
                                <div className="flex gap-1 bg-black/30 p-1 rounded-lg border border-white/5 shadow-inner">
                                    {[
                                        { value: 'left', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></svg> },
                                        { value: 'center', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="10" x2="6" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="18" y1="18" x2="6" y2="18" /></svg> },
                                        { value: 'right', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="10" x2="7" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="7" y2="18" /></svg> },
                                        // Dernière ligne COURTE sur l'icône : elle montre ce
                                        // qu'elle fait, et distingue les deux justifications
                                        // au premier regard. Même dessin que dans la barre
                                        // flottante de l'éditeur, les deux jeux devant être
                                        // identiques.
                                        { value: 'justify', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="10" x2="3" y2="10" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></svg> }
                                        ,
                                        // Justification à DERNIÈRE LIGNE ÉTIRÉE. Le bouton
                                        // « justifié » ci-dessus applique désormais la
                                        // typographie courante — dernière ligne libre, comme
                                        // Illustrator et InDesign. Étirer la dernière ligne
                                        // d'un bord à l'autre est un cas rare et volontaire,
                                        // il lui fallait donc son propre bouton plutôt que
                                        // d'être le comportement par défaut, ce qu'il était.
                                        { value: 'justify', lastLine: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="10" x2="3" y2="10" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="3" y2="18" /><line x1="22.4" y1="3" x2="22.4" y2="21" /></svg> }
                                    ].map(align => {
                                        // Un paragraphe sans `lastLineJustify` étire sa dernière
                                        // ligne (comportement historique, préservé pour ne pas
                                        // modifier l'allure des documents déjà enregistrés).
                                        const paraAttrs = el.content?.content?.[0]?.attrs;
                                        const alignCourant = paraAttrs?.textAlign || el.textAlign;
                                        const derniereEtiree = paraAttrs?.lastLineJustify !== false;
                                        const actif = align.value === 'justify'
                                            ? (alignCourant === 'justify' && derniereEtiree === !!align.lastLine)
                                            : alignCourant === align.value;
                                        const libelle = align.value !== 'justify'
                                            ? (LIBELLES_ALIGNEMENT[align.value] || 'Alignement du texte')
                                            : (align.lastLine ? 'Justifié, dernière ligne étirée'
                                                              : 'Justifié, dernière ligne libre');
                                        return (
                                        <button key={align.value + (align.lastLine ? '-all' : '')} onClick={() => {
                                            // Sync alignement : on met à jour el.textAlign (CSS global)
                                            // ET les paragraphes Tiptap dans el.content si présent
                                            updateSingle('textAlign', align.value);
                                            if (el.content?.content) {
                                                const newContent = {
                                                    ...el.content,
                                                    content: el.content.content.map(p => ({
                                                        ...p,
                                                        attrs: {
                                                            ...(p.attrs || {}), textAlign: align.value,
                                                            // Posé explicitement dans les deux sens : sans ça,
                                                            // repasser de « étiré » à « libre » laisserait
                                                            // l'ancienne valeur en place.
                                                            ...(align.value === 'justify'
                                                                ? { lastLineJustify: !!align.lastLine } : {}),
                                                        }
                                                    }))
                                                };
                                                updateSingle('content', newContent);
                                            }
                                        }} title={libelle} aria-label={libelle} className={`flex-1 flex justify-center items-center py-1.5 rounded-md text-xs transition-all ${actif ? 'bg-white text-black shadow-md' : 'text-white/40 hover:text-white hover:bg-white/10'}`}>
                                            {align.icon}
                                        </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* POSITIONNEMENT BRUT (Disabled if locked) */}
                        <div className={`grid grid-cols-2 gap-3 pt-2 ${el.locked ? 'opacity-50 pointer-events-none' : ''}`}>
                            {/* Bornes larges, parce qu'elles sont désormais APPLIQUÉES et
                                que le reste de l'éditeur produit couramment des valeurs
                                que les anciennes bornes décoratives interdisaient : un
                                calque déposé ou un repère tiré dans le fond perdu a un x
                                ou un y négatif (coordonnées comptées depuis le bord du
                                format, fond perdu retiré), le déplacement au clavier n'a
                                aucune limite, et une poignée de redimensionnement descend
                                jusqu'à 0,1 mm. Avec un minimum à 0 et à 1, un calque à
                                x = −2 mm sautait à 0 au premier clic sur − ou +, et un
                                trait de 0,3 mm était épaissi à 1 mm — la destruction
                                annoncée comme une correction. */}
                            {renderNumberInput('Pos X', 'x', el.x, -1000, 1000, 0.1)}
                            {renderNumberInput('Pos Y', 'y', el.y, -1000, 1000, 0.1)}
                            {renderNumberInput('Largeur', 'width', el.width, 0.1, 1000, 0.1)}
                            {renderNumberInput('Hauteur', 'height', el.height, 0.1, 1000, 0.1)}
                        </div>

                        {/* QUICK RESIZE (image / svg layers only) */}
                        {(el.type === 'image' || el.type === 'shape') && !el.locked && (
                            <div className="flex items-center gap-2 pt-2">
                                <label className="text-[9px] text-white/50 uppercase tracking-widest font-mono shrink-0">Réduire</label>
                                <div className="flex gap-1 flex-1">
                                    {[0.75, 0.5, 0.25].map((factor) => (
                                        <button
                                            key={factor}
                                            onClick={() => {
                                                const newW = Math.round((el.width * factor) * 10) / 10;
                                                const newH = Math.round((el.height * factor) * 10) / 10;
                                                // Keep the layer centered on its current center point
                                                const cx = el.x + el.width / 2;
                                                const cy = el.y + el.height / 2;
                                                updateElement(el.id, {
                                                    width: newW,
                                                    height: newH,
                                                    x: Math.round((cx - newW / 2) * 10) / 10,
                                                    y: Math.round((cy - newH / 2) * 10) / 10,
                                                });
                                            }}
                                            className="flex-1 text-[10px] font-mono font-bold text-white/60 hover:text-white bg-black/30 hover:bg-white/10 border border-white/10 hover:border-white/25 rounded-lg py-1.5 transition-all"
                                            title={`Réduire le calque à ${Math.round(factor * 100)}% de sa taille actuelle (centre conservé)`}
                                        >
                                            {Math.round(factor * 100)}%
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* RÉGLAGES D'IMAGE (flou, tonalité, niveaux, couleur)
                            APRÈS la grille de position et « Réduire », à la demande de
                            l'utilisateur : placé plus haut, entre le Miroir et
                            l'alignement, le bloc se noyait dans la suite des réglages de
                            forme et passait inaperçu. */}
                        {el.type === 'image' && <ReglagesImagePanel el={el} />}

                        {/* Aligner un calque SEUL sur le cadre de l'étiquette. Le bloc
                            n'existait qu'en sélection multiple, et le store refusait
                            l'opération sous deux cibles : centrer un objet seul, le geste
                            le plus courant d'Illustrator, n'était possible qu'en calculant
                            sa position à la main. */}
                        {!el.locked && el.type !== 'guide' && (
                            <div className="mt-6 pt-4 border-t border-white/5">
                                <BoutonsAlignement ids={[el.id]} surEtiquette />
                            </div>
                        )}

                        {el.linkedId && (
                            <div className="mt-6 pt-4 border-t border-white/5">
                                <div className="flex flex-col gap-2 text-xs">
                                    <span className="text-[9px] text-white/40 uppercase tracking-widest font-mono">Groupe Lié ({el.linkedId.substring(0, 8)})</span>
                                    <button onClick={() => unlinkElement(el.id)} className="w-full py-2 bg-red-900/20 hover:bg-red-900/40 text-red-400 border border-red-900/50 text-[10px] rounded-lg transition-colors font-bold tracking-widest uppercase shadow-inner">
                                        Délier cet élément
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* MULTI-SÉLECTION : Alignement / Distribution */}
                {isMultiSelection && (
                    <div className="space-y-4 flex-1 mb-6">
                        <div className="flex justify-between items-center pb-3 mb-2 border-b border-white/5">
                            <h3 className="text-white text-[10px] tracking-widest font-mono uppercase font-bold">{selectedElementIds.length} éléments</h3>
                        </div>

                        <BoutonsAlignement ids={selectedElementIds} surEtiquette={false} />

                        {/* Distribution */}
                        {selectedElementIds.length >= 3 && (
                            <div>
                                <label className="block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-2">Distribuer</label>
                                <div className="grid grid-cols-2 gap-1 bg-black/30 p-1 rounded-lg border border-white/5 shadow-inner">
                                    <button
                                        onClick={() => { distributeElements(selectedElementIds, 'horizontal'); setTimeout(() => useLabelStore.getState()._pushState('Distribution horizontale'), 0); }}
                                        className="flex items-center justify-center gap-2 py-2 rounded-md text-white/40 hover:bg-white/10 hover:text-white transition-all text-[9px] font-mono uppercase"
                                        title="Espacement horizontal égal" aria-label="Espacement horizontal égal"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="6" width="4" height="12" rx="1"/><rect x="10" y="8" width="4" height="8" rx="1"/><rect x="17" y="6" width="4" height="12" rx="1"/></svg>
                                        Horizontal
                                    </button>
                                    <button
                                        onClick={() => { distributeElements(selectedElementIds, 'vertical'); setTimeout(() => useLabelStore.getState()._pushState('Distribution verticale'), 0); }}
                                        className="flex items-center justify-center gap-2 py-2 rounded-md text-white/40 hover:bg-white/10 hover:text-white transition-all text-[9px] font-mono uppercase"
                                        title="Espacement vertical égal" aria-label="Espacement vertical égal"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="3" width="12" height="4" rx="1"/><rect x="8" y="10" width="8" height="4" rx="1"/><rect x="6" y="17" width="12" height="4" rx="1"/></svg>
                                        Vertical
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Grouper — le commentaire annonçait « Grouper / Dégrouper » alors que ce
                            bloc n'a jamais contenu que le bouton Grouper, et qu'aucun Dégrouper
                            n'existe : l'opération inverse est « Délier cet élément », qui détache
                            un seul calque de son groupe et vit dans la branche de sélection
                            simple, jamais affichée en même temps que celle-ci. Le commentaire
                            envoyait donc chercher ici un contrôle qui n'y est pas. */}
                        <div className="pt-3 border-t border-white/5">
                            <button
                                onClick={() => { linkElements(selectedElementIds); setTimeout(() => useLabelStore.getState()._pushState('Groupement'), 0); }}
                                className="w-full py-2.5 bg-amber-900/20 hover:bg-amber-900/40 text-amber-400 border border-amber-900/50 text-[10px] rounded-lg transition-colors font-bold tracking-widest uppercase shadow-inner"
                            >
                                Grouper ({selectedElementIds.length})
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
